import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useCrmAuth } from "./CrmAuth";
import { ArrowUpRight, Loader2 } from "lucide-react";

export default function CrmLogin() {
  const { login, error, loading, user } = useCrmAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();

  useEffect(() => {
    if (user) nav("/crm", { replace: true });
  }, [user, nav]);
  if (user) return null;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const ok = await login(email.trim(), password);
    setBusy(false);
    if (ok) nav("/crm", { replace: true });
  };

  return (
    <div className="min-h-screen bg-[#08080b] text-white grid lg:grid-cols-2">
      {/* Left panel — brand */}
      <div className="hidden lg:flex relative overflow-hidden bg-[#0b0b10] p-14 flex-col justify-between">
        <div className="absolute -left-40 top-1/3 w-[560px] h-[560px] rounded-full bg-[#1F5EFF]/12 blur-[120px]" />
        <div className="relative z-10">
          <div className="flex items-center gap-3 mb-3">
            <span className="w-8 h-px bg-[#1F5EFF]" />
            <span className="font-mono-alt text-[10px] text-[#1F5EFF]">WTD · CRM STUDIO</span>
          </div>
          <div className="font-display text-5xl lg:text-6xl leading-[0.95] tracking-[-0.03em] max-w-md">
            The studio. <span className="text-white/40">Digitised.</span>
          </div>
        </div>
        <div className="relative z-10 font-mono-alt text-[10px] text-white/40 leading-relaxed max-w-sm">
          Appointments · Check-Ins · Inspections · Work Orders — the entire studio floor in one place. Built for iPad, iPhone and desktop.
        </div>
      </div>

      {/* Right panel — form */}
      <div className="flex items-center justify-center p-8 md:p-14">
        <form onSubmit={submit} className="w-full max-w-sm" data-testid="crm-login-form">
          <div className="mb-10">
            <h1 className="font-display text-3xl md:text-4xl tracking-tight">Sign in</h1>
            <p className="text-white/50 text-sm mt-2">Enter your studio credentials to continue.</p>
          </div>
          {error && (
            <div className="mb-4 p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-300 text-sm" data-testid="crm-login-error">
              {error}
            </div>
          )}
          <label className="block mb-4">
            <div className="text-[11px] font-mono-alt text-white/50 mb-1.5 tracking-widest">EMAIL</div>
            <input
              type="email" required
              value={email} onChange={(e) => setEmail(e.target.value)}
              className="w-full bg-white/5 border border-white/12 rounded-lg px-4 py-3 outline-none focus:border-[#1F5EFF] transition-colors"
              data-testid="crm-login-email" autoComplete="email"
            />
          </label>
          <label className="block mb-6">
            <div className="text-[11px] font-mono-alt text-white/50 mb-1.5 tracking-widest">PASSWORD</div>
            <input
              type="password" required
              value={password} onChange={(e) => setPassword(e.target.value)}
              className="w-full bg-white/5 border border-white/12 rounded-lg px-4 py-3 outline-none focus:border-[#1F5EFF] transition-colors"
              data-testid="crm-login-password" autoComplete="current-password"
            />
          </label>
          <button
            type="submit" disabled={busy || loading}
            data-testid="crm-login-submit"
            className="w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-lg bg-[#1F5EFF] hover:bg-white hover:text-black text-white font-medium transition-colors disabled:opacity-50"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Continue <ArrowUpRight className="w-4 h-4" /></>}
          </button>
          <div className="mt-6 text-xs text-white/40 text-center">
            <Link to="/" className="hover:text-white">← Back to wtddetailing.ca</Link>
          </div>
        </form>
      </div>
    </div>
  );
}
